import { useMemo } from "react";
import { Machine } from "@/machines/types/machine";
import { Gps } from "@/machines/types/gps";
import { CoordinateSet as MapCoordinateSet } from "@/map";
import { Polygon } from "@/polygon/types/polygon";
import { stringToColor } from "@/shared/utils/color/string-to-color";

export function useMachineDetailMapCoordinates(
  machine?: Machine,
  gpsList?: Gps[],
  selectedTime?: number | string | null,
  selectedPolygons?: Polygon[]
): { basicCoordinates: MapCoordinateSet[]; polygonCoordinates: MapCoordinateSet[] } {
  const basicCoordinates = useMemo<MapCoordinateSet[]>(() => {
    if (!machine || !gpsList) return [];

    const filteredPath = gpsList
      .filter((gps) => gps.latitude && gps.longitude)
      .map((gps) => ({
        lat: gps.latitude,
        lng: gps.longitude,
        alt: gps.altitude,
        time: new Date(gps.timestamp).toISOString(),
        color: gps.color,
      }));

    let currentIndex = -1;
    if (selectedTime != null) {
      const selectedTimestamp = typeof selectedTime === 'number' ? selectedTime : new Date(selectedTime).getTime();
      let minDiff = Infinity;
      filteredPath.forEach((p, idx) => {
        const pTime = new Date(p.time!).getTime();
        const diff = Math.abs(pTime - selectedTimestamp);
        if (diff < minDiff) {
          minDiff = diff;
          currentIndex = idx;
        }
      });
    } else if (filteredPath.length > 0) {
      currentIndex = filteredPath.length - 1;
    }


    const pathWithCurrent = filteredPath.map((p, idx) => ({
      ...p,
      current: idx === currentIndex,
    }));

    return [
      {
        name: machine.name,
        color: machine.color || "#ffff00",
        path: pathWithCurrent,
        isActive: machine.status === "online",
        markerType: 'gps' as const,
        disablePolyline: true,
      },
    ];
  }, [machine, gpsList, selectedTime]);

  const polygonCoordinates = useMemo<MapCoordinateSet[]>(() => {
    if (!selectedPolygons || selectedPolygons.length === 0) return [];
    return selectedPolygons
      .filter(p => p.coordinates && p.coordinates.length > 0)
      .map((polygon) => ({
        name: polygon.name,
        color: stringToColor(polygon.name),
        path: polygon.coordinates.map((coord) => ({
          lat: coord.latitude,
          lng: coord.longitude,
        })),
        isActive: true,
      }));
  }, [selectedPolygons]);

  return { basicCoordinates, polygonCoordinates };
}
